import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import idx from 'idx';
import AddUserMenu from './add-user-menu';
import TimesheetTooltip from './timesheet-tooltip';

const TimesheetGridWrapper = ({
    dates,
    selectedUsers,
    timesheetData,
    users,
    projectId,
    hasProjectAccess,
    writePermission,
    onAddRow,
    onRemoveRow,
    onCellClick,
    calculateDailyTotal,
    calculateWeeklyTotal,
    calculateGrandDailyTotal
}) => {
    const isAddRowDisabled = !hasProjectAccess || !projectId;
    const getAddRowTooltip = () => {
        if (!hasProjectAccess) return "You don't have access to any projects";
        if (!projectId) return "Please select a project first";
        return "";
    };

    return (
        <div className="timesheet-grid-wrapper">
            <div className="timesheet-grid">
                {/* Table Header */}
                <div className="grid-header grid-row">
                    <div className="header-cell employee-header">
                        <span className="emp-label">Employee</span>
                    </div>
                    {dates.map(date => (
                        <div key={date.toString()} className="header-cell">
                            <span className="day-name">{date.format('ddd')}</span>
                            <span className="day-date">{date.format('DD/MM')}</span>
                        </div>
                    ))}
                    <div className="header-cell">Weekly Total</div>
                    <div className="header-cell action-header">
                        {/* Add Row Button */}
                        {writePermission && (
                            <Tooltip
                                title={getAddRowTooltip()}
                                placement="top"
                            >
                                <span>
                                    <AddUserMenu
                                        users={users}
                                        selectedUsers={selectedUsers}
                                        onAdd={(ids) => ids.forEach(id => onAddRow(id))}
                                        disabled={isAddRowDisabled}
                                        renderTrigger={(onClick, disabled) => (
                                            <IconButton
                                                size="small"
                                                onClick={onClick}
                                                disabled={disabled}
                                                className="add-row-icon-btn"
                                            >
                                                <AddIcon />
                                            </IconButton>
                                        )}
                                    />
                                </span>
                            </Tooltip>
                        )}
                    </div>
                </div>

                <div className="grid-body">
                    {selectedUsers.length === 0 && (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                            No employees added yet. Click the + icon on the header to begin.
                        </div>
                    )}
                    {selectedUsers.map(user => (
                        <div key={user.value} className="body-row grid-row">
                            <div className="employee-cell">
                                {user.key}
                            </div>
                            {dates.map(date => {
                                const dateStr = date.format('YYYY-MM-DD');
                                const dayEntries = idx(timesheetData, _ => _[user.value][dateStr]) || [];
                                const total = calculateDailyTotal(dayEntries);

                                return (
                                    <div
                                        key={dateStr}
                                        className="day-cell"
                                        onClick={() => onCellClick(user.value, date)}
                                    >
                                        <TimesheetTooltip entries={dayEntries}>
                                            <div className="day-cell-content">
                                                <span className={total > 0 ? "total-hours" : "total-hours zero-hours"}>
                                                    {total.toFixed(1)}
                                                </span>
                                                {dayEntries.length > 0 && (
                                                    <span className="entry-count">({dayEntries.length} entries)</span>
                                                )}
                                            </div>
                                        </TimesheetTooltip>
                                    </div>
                                );
                            })}
                            <div className="total-cell">
                                {calculateWeeklyTotal(user.value).toFixed(1)}
                            </div>
                            <div className="action-cell">
                                {writePermission && (
                                    <IconButton size="small" onClick={() => onRemoveRow(user.value)}>
                                        <DeleteIcon style={{ color: '#ef4444' }} />
                                    </IconButton>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="grid-footer grid-row">
                    <div className="footer-cell label-cell">Daily Totals</div>
                    {dates.map(date => (
                        <div key={date.toString()} className="footer-cell">
                            {calculateGrandDailyTotal(date.format('YYYY-MM-DD')).toFixed(1)}
                        </div>
                    ))}
                    <div className="footer-cell">
                        {dates.reduce((acc, date) => acc + calculateGrandDailyTotal(date.format('YYYY-MM-DD')), 0).toFixed(1)}
                    </div>
                    <div className="footer-cell"></div>
                </div>
            </div>
        </div>
    );
};

export default TimesheetGridWrapper;
